/**
 * api/executions.js — queries e mutations para execuções, squad e gates.
 *
 * Endpoints disponíveis (Ondas 3–5):
 *
 * Squad:
 *   GET  /executions/{id}/squad
 *     → { squad_id, squad_name, mission, color_token, nodes:[...], edges:[...], estimate:{...} }
 *
 * Gate A (por execução):
 *   GET  /executions/{id}/gates/a
 *     → { gate_id, execution_id, status, sla_tier, approvers[], decided_by, decided_at,
 *          estimate_tokens, estimate_cost_usd, rejection_reason,
 *          requirements:[{ requirement_id, title, description, priority, category, origin,
 *                          is_approved, external_ref, criteria:[{criterion_id, description, is_met}] }] }
 *     404 quando o gate ainda não foi criado.
 *   PATCH /executions/{id}/gates/a
 *     body: { decision: 'approve'|'reject'|'clarify', comment? }
 *     Requer permissão quality_gates:approve_gate. Pode retornar 403.
 *
 * Gate B (por execução) — Onda C:
 *   GET  /executions/{id}/gates/b
 *     → { gate_id, execution_id, status, sla_tier, approvers[], estimate_tokens,
 *          estimate_cost_usd, comment, plan_tasks:[{task_id,title,priority,estimate_points,status}],
 *          created_at, decided_at }
 *     404 quando o gate ainda não foi criado.
 *   PATCH /executions/{id}/gates/b
 *     body: { decision: 'approve'|'reject'|'clarify', comment? }
 *     comment obrigatório em reject/clarify. Requer quality_gates:approve_gate. Pode retornar 403.
 *
 * Board (Kanban — Onda C):
 *   GET  /executions/{id}/board
 *     → { execution_id, columns:{<coluna>:[{card_id,agent_slug,title,status,tests:[]}]},
 *          recent_moves:[{move_id,card_id,from_column,to_column,moved_at}] }
 *
 * Output (Onda D):
 *   GET  /executions/{id}/output
 *     → { execution_id,
 *          pull_request:{
 *            pr_id, title, body, pr_url, commit_sha, head_branch, base_branch,
 *            provider, external_pr_id, is_simulated, diff_url
 *          },
 *          code_artifacts: [{
 *            artifact_id, path, action(A|M|D), additions, deletions,
 *            is_simulated, content_sha, sandbox_run_id, diff
 *          }],
 *          artifacts_total: number,
 *          compliance_checks:[{check_id,rule_name,status,detail,checked_at}],
 *          audit_events:[{seq,event_type,actor_type,occurred_at}],
 *          replay_events:[{seq,event_type,actor_type,occurred_at}] }
 *   Query params: artifacts_limit / artifacts_offset (paginação de artefatos)
 *
 * Fila de gates do tenant:
 *   GET  /gates
 *     → { total, items:[{ gate_id, execution_id, gate_type, status, sla_tier, approvers[], created_at }] }
 *     gate_type: 'a' | 'b' — usado para rotear Gate A vs Gate B na fila.
 *
 * Execução (status):
 *   GET  /executions/{id}
 *     → { execution_id, status, project_name, ... }
 *
 * Convenções:
 *   - executionKeys segue hierarquia: ['executions', id, sub-recurso]
 *   - gateKeys     segue hierarquia: ['gates', ...]
 *   - Mutation de decisão invalida a gate e a fila após sucesso.
 *   - ApiError 403 → ForbiddenBanner via isForbidden.
 *   - 404 em GET /gates/a ou /gates/b → data === null (gate não criado).
 *   - Nunca exibir wallet_txn_id na UI.
 *   - Clean: sem lógica de UI aqui.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, ApiError } from './client';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const executionKeys = {
  all:       ()                         => ['executions'],
  detail:    (id)                       => ['executions', id],
  squad:     (id)                       => ['executions', id, 'squad'],
  gateA:     (id)                       => ['executions', id, 'gates', 'a'],
  gateB:     (id)                       => ['executions', id, 'gates', 'b'],
  board:     (id)                       => ['executions', id, 'board'],
  output:    (id)                       => ['executions', id, 'output'],
  artifacts: (id, limit, offset)        => ['executions', id, 'artifacts', { limit, offset }],
  bancada:   (id)                       => ['executions', id, 'bancada'],
};

export const gateKeys = {
  all:    ()           => ['gates'],
  queue:  (filters)    => ['gates', 'queue', filters ?? {}],
};

// ─── Execução ─────────────────────────────────────────────────────────────────

/**
 * useStartExecution — inicia o pipeline (Temporal) de um projeto.
 * POST /executions {project_id} → 202 {execution_id, run_id, status}.
 * O backend vincula a execução ao projeto (status='running', temporal_workflow_id).
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useStartExecution() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId) => api.post('/executions', { project_id: projectId }),
    onSuccess: () => {
      // Atualiza a lista de projetos (status mudou para 'running').
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * useRegenerateCode — re-executa o projeto reaproveitando a definição, com os
 * gates HUMANOS auto-aprovados (o design já foi revisado). Vai direto gerar o
 * código de novo (com as correções), sem refazer a jornada nem travar nos gates.
 *
 * POST /executions { project_id, extra: { auto_approve_gates, regenerate } }.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useRegenerateCode() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId) => api.post('/executions', {
      project_id: projectId,
      extra: { auto_approve_gates: true, regenerate: true },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * useContinueExecution — CONTINUAR DE ONDE PAROU (2026-10-08): uma execução nova que herda o
 * que a última execução encerrada do projeto já fechou. As etapas que fecharam não rodam de
 * novo; as que ficaram com pendência são conferidas de novo e corrigidas; o que faltava é
 * construído. O escopo, o contrato aprovado e o design são os mesmos.
 *
 * POST /executions/continue { project_id, from_execution_id?, refazer_camadas? }.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useContinueExecution() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alvo) => {
      const p = typeof alvo === 'string' ? { projectId: alvo } : (alvo || {});
      return api.post('/executions/continue', {
        project_id: p.projectId,
        ...(p.fromExecutionId ? { from_execution_id: p.fromExecutionId } : {}),
        ...(p.refazerCamadas?.length ? { refazer_camadas: p.refazerCamadas } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * useRemediateCode — REMEDIAÇÃO ESTRITA (P2): re-executa em modo remediação, corrigindo
 * ESTRITAMENTE os itens do .md da Auditoria de Entrega (gaps), no repo existente
 * (brownfield). Mesmo caminho do regenerar, com extra.remediation=true.
 *
 * POST /executions { project_id, extra: { auto_approve_gates, regenerate, remediation } }.
 */
export function useRemediateCode() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId) => api.post('/executions', {
      project_id: projectId,
      extra: { auto_approve_gates: true, regenerate: true, remediation: true },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * useRemediate — REMEDIAÇÃO DIRIGIDA PELO CLIENTE ("Remediar por prompt", tipo Claude Code
 * do produto): completa/conserta a entrega existente a partir de um TEXTO LIVRE do cliente
 * e/ou de uma SUPERFÍCIE-alvo (web|mobile). Brownfield que acumula; PR como proposta.
 *
 * POST /executions/remediate { project_id, prompt, surface }.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useRemediate() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, prompt = '', surface = '' }) =>
      api.post('/executions/remediate', { project_id: projectId, prompt, surface }),
    // Sucesso OU 409 (já havia execução ativa): a "última execução" mudou/precisa refletir.
    onSettled: (_data, _error, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['latest-execution', projectId] });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * useCancelExecution — PARA (termina) uma execução em andamento.
 *
 * POST /executions/{id}/cancel — corta o gasto de tokens na hora (terminate no
 * Temporal). Idempotente no backend. Invalida lista/detalhe ao concluir.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useCancelExecution() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (executionId) => api.post(`/executions/${executionId}/cancel`),
    onSuccess: (_data, executionId) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (executionId) {
        queryClient.invalidateQueries({ queryKey: executionKeys.detail(executionId) });
      }
    },
  });
}

/**
 * useStopProject — para o projeto e QUALQUER execução ativa dele.
 *
 * POST /projects/{id}/stop.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useStopProject() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId) => api.post(`/projects/${projectId}/stop`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * useDeleteProject — exclui o projeto (soft-delete). O backend PARA execuções
 * ativas antes de excluir, então nenhum workflow fica órfão queimando tokens.
 *
 * DELETE /projects/{id}.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useDeleteProject() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId) => api.delete(`/projects/${projectId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * useExecutionTrace — trace/waterfall da execução (Mission Control).
 * Projeta timeline_events em spans por fase. Read-only (não depende do Temporal).
 * @param {string|null} executionId
 * @param {{refetchInterval?: number|function}} [opts]
 */
export function useExecutionTrace(executionId, { refetchInterval } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: [...executionKeys.detail(executionId), 'trace'],
    queryFn:  () => api.get(`/executions/${executionId}/trace`),
    enabled:  Boolean(executionId),
    staleTime: 5_000,
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

/**
 * useExecution — status + metadados de uma execução.
 * @param {string|null} executionId
 */
export function useExecution(executionId, { refetchInterval } = {}) {
  const api = useApi();

  return useQuery({
    queryKey: executionKeys.detail(executionId),
    queryFn:  () => api.get(`/executions/${executionId}`),
    enabled:  Boolean(executionId),
    staleTime: 15_000,
    // Live Board pode pollar o status (função ou ms) enquanto a execução roda —
    // assim o "terminal" que encerra o polling dos gates é detectado sem refresh.
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

// ─── Squad ────────────────────────────────────────────────────────────────────

/**
 * useExecutionSquad — grafo do squad montado pelo organizer para uma execução.
 *
 * Resposta: {
 *   squad_id: string|null,
 *   squad_name: string,
 *   mission: string,
 *   color_token: string,
 *   nodes: Array<{
 *     node_id, agent_id, node_label, tier, model_override,
 *     max_tokens, standards[], node_type, position_x, position_y
 *   }>,
 *   edges: Array<{
 *     edge_id, source_node, target_node, edge_label, edge_type, condition
 *   }>,
 *   estimate: { total_tokens, total_cost_usd }
 * }
 *
 * Retorna { data: null } quando squad_id === null (squad ainda não montado).
 *
 * @param {string|null} executionId
 */
export function useExecutionSquad(executionId) {
  const api = useApi();

  return useQuery({
    queryKey: executionKeys.squad(executionId),
    queryFn:  () => api.get(`/executions/${executionId}/squad`),
    enabled:  Boolean(executionId),
    staleTime: 5_000,
    // O squad é gerado no passo organize_squad, ~30-60s após iniciar a execução.
    // Faz polling (4s) enquanto ainda não há nós; para assim que o squad existe.
    refetchInterval: (query) => {
      const nodes = query.state.data?.nodes;
      return (Array.isArray(nodes) && nodes.length > 0) ? false : 4000;
    },
  });
}

/** Edita a config de um nó do squad (Bloco 3) — PUT /executions/{id}/squad/nodes/{nodeId}. */
export function useEditSquadNode(executionId) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeId, ...body }) =>
      api.put(`/executions/${executionId}/squad/nodes/${nodeId}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: executionKeys.squad(executionId) }),
  });
}

/** Re-sugere o squad via organizer real (Claude) — POST /executions/{id}/squad/resuggest. */
export function useResuggestSquad(executionId) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    mutationFn: () => api.post(`/executions/${executionId}/squad/resuggest`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: executionKeys.squad(executionId) }),
  });
}

// ─── Gate A (por execução) ────────────────────────────────────────────────────

/**
 * useExecutionGateA — requisitos e status do Gate A de uma execução.
 *
 * 404 é normalizado para null (gate não criado ainda).
 *
 * @param {string|null} executionId
 */
export function useExecutionGateA(executionId, { refetchInterval } = {}) {
  const api = useApi();

  return useQuery({
    queryKey: executionKeys.gateA(executionId),
    queryFn:  async () => {
      try {
        return await api.get(`/executions/${executionId}/gates/a`);
      } catch (err) {
        // 404 = gate ainda não criado — retornar null é semântica de domínio
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled:  Boolean(executionId),
    staleTime: 15_000,
    // Live Board pode passar refetchInterval (função ou ms) para o status de
    // aprovação refletir ao vivo sem refresh manual.
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

/**
 * useDecideGateA — mutation para aprovar/rejeitar/pedir esclarecimento no Gate A.
 *
 * Após sucesso invalida:
 *   - Gate A da execução (executionKeys.gateA)
 *   - Fila global de gates (gateKeys.all)
 *   - Status da execução (executionKeys.detail)
 *
 * 403 propagado via error.isForbidden para o componente exibir ForbiddenBanner.
 *
 * @param {string} executionId
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useDecideGateA(executionId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    /**
     * @param {{ decision: 'approve'|'reject'|'clarify', comment?: string }} payload
     */
    mutationFn: (payload) =>
      api.patch(`/executions/${executionId}/gates/a`, payload),

    onSuccess: () => {
      // Revalida gate, fila e execução
      queryClient.invalidateQueries({ queryKey: executionKeys.gateA(executionId) });
      queryClient.invalidateQueries({ queryKey: executionKeys.detail(executionId) });
      queryClient.invalidateQueries({ queryKey: gateKeys.all() });
    },
  });
}

/**
 * useEditRequirement — edita um requisito do Gate A (classificação MoSCoW,
 * categoria, aprovação individual). PATCH /executions/{id}/requirements/{rid}.
 * @param {string} executionId
 */
export function useEditRequirement(executionId) {
  const api         = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    // { requirementId, priority?, category?, approval? }
    mutationFn: ({ requirementId, ...patch }) =>
      api.patch(`/executions/${executionId}/requirements/${requirementId}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: executionKeys.gateA(executionId) });
    },
  });
}

/**
 * useWaiveComplianceCheck — concede/retira waiver de um compliance_check do Gate B.
 * PATCH /executions/{id}/compliance-checks/{checkId} { waiver, waiver_reason? }.
 * @param {string} executionId
 */
export function useWaiveComplianceCheck(executionId) {
  const api         = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ checkId, ...body }) =>
      api.patch(`/executions/${executionId}/compliance-checks/${checkId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: executionKeys.output(executionId) });
    },
  });
}

/**
 * useEditarRoteiro — grava o roteiro de validação (projeto de agente) no contrato pendente.
 * PUT /executions/{id}/gates/b/roteiro { cases }. 422 traz `errors` com a lista por caso.
 * @param {string} executionId
 */
export function useEditarRoteiro(executionId) {
  const api         = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cases) => api.put(`/executions/${executionId}/gates/b/roteiro`, { cases }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: executionKeys.gateB(executionId) });
    },
  });
}

// ─── Bancada do agente ("Experimentar o agente") ──────────────────────────────

/**
 * useBancada — rodadas da bancada, casos do roteiro e casos guardados no projeto.
 * GET /executions/{id}/bancada. Enquanto há rodada em andamento, consulta a cada 3 s.
 * @param {string|null} executionId
 */
export function useBancada(executionId) {
  const api = useApi();
  return useQuery({
    queryKey: executionKeys.bancada(executionId),
    queryFn: () => api.get(`/executions/${executionId}/bancada`),
    enabled: !!executionId,
    refetchInterval: (query) => (
      (query.state.data?.rodadas ?? []).some((r) => r.status === 'pending' || r.status === 'running')
        ? 3000 : false),
  });
}

/** usePedirRodada — POST /executions/{id}/bancada { modelo, case_id?, entrada? }. */
export function usePedirRodada(executionId) {
  const api         = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pedido) => api.post(`/executions/${executionId}/bancada`, pedido),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: executionKeys.bancada(executionId) }),
  });
}

/** useGuardarCasoDaBancada — POST /executions/{id}/bancada/{runId}/caso. */
export function useGuardarCasoDaBancada(executionId) {
  const api         = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, ...caso }) => api.post(`/executions/${executionId}/bancada/${runId}/caso`, caso),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: executionKeys.bancada(executionId) }),
  });
}

/** useRemoverCasoDoProjeto — DELETE /executions/{id}/bancada/casos/{caseId}. */
export function useRemoverCasoDoProjeto(executionId) {
  const api         = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId) => api.delete(`/executions/${executionId}/bancada/casos/${caseId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: executionKeys.bancada(executionId) }),
  });
}

// ─── Fila de Gates do Tenant ──────────────────────────────────────────────────

/**
 * useGatesQueue — fila de gates pendentes do tenant.
 *
 * Resposta: { total: number, items: GateQueueItem[] }
 * GateQueueItem: { gate_id, execution_id, gate_type, status, sla_tier, approvers[], created_at }
 * gate_type: 'a' | 'b' — usado para rotear o gate correto na fila.
 *
 * Nunca exibe wallet_txn_id.
 *
 * @param {{ status?: string, limit?: number, offset?: number }} filters
 */
export function useGatesQueue(filters = {}) {
  const api = useApi();

  const params = new URLSearchParams();
  if (filters.status)        params.set('status',  filters.status);
  if (filters.limit != null) params.set('limit',   String(filters.limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  const qs = params.toString();

  return useQuery({
    queryKey: gateKeys.queue(filters),
    queryFn:  () => api.get(`/gates${qs ? `?${qs}` : ''}`),
    staleTime: 15_000,
    // Polling a cada 30s para manter a fila atualizada sem SSE
    refetchInterval: 30_000,
  });
}

// ─── Board (Kanban — Onda C) ──────────────────────────────────────────────────

/**
 * useExecutionBoard — estado inicial do Kanban para uma execução.
 *
 * Usado para hidratação inicial; atualizações ao vivo chegam via SSE (kanban events).
 *
 * Resposta: {
 *   execution_id: string,
 *   columns: {
 *     now: CardItem[],
 *     planning: CardItem[],
 *     todo: CardItem[],
 *     backingCoding: CardItem[],
 *     coding: CardItem[],
 *     backingQA: CardItem[],
 *     testing: CardItem[],
 *     done: CardItem[],
 *   },
 *   recent_moves: Array<{move_id, card_id, from_column, to_column, moved_at}>
 * }
 *
 * CardItem: { card_id, agent_slug, title, status, tests:[] }
 *
 * Nunca exibe wallet_txn_id.
 *
 * @param {string|null} executionId
 */
export function useExecutionBoard(executionId) {
  const api = useApi();

  return useQuery({
    queryKey: executionKeys.board(executionId),
    queryFn: async () => {
      try {
        return await api.get(`/executions/${executionId}/board`);
      } catch (err) {
        // 404 → board ainda não iniciado → null
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled:  Boolean(executionId),
    staleTime: 10_000,
    // Rehidrata o board a cada 60s como fallback caso eventos SSE caiam
    refetchInterval: 60_000,
  });
}

// ─── Gate B (por execução — Onda C) ──────────────────────────────────────────

/**
 * useExecutionGateB — plano de tasks e status do Gate B de uma execução.
 *
 * 404 é normalizado para null (gate não criado ainda).
 *
 * Resposta: {
 *   gate_id, execution_id, status, sla_tier,
 *   approvers[], estimate_tokens, estimate_cost_usd, comment,
 *   plan_tasks: [{task_id, title, priority, estimate_points, status}],
 *   created_at, decided_at
 * }
 *
 * @param {string|null} executionId
 */
export function useExecutionGateB(executionId, { refetchInterval } = {}) {
  const api = useApi();

  return useQuery({
    queryKey: executionKeys.gateB(executionId),
    queryFn:  async () => {
      try {
        return await api.get(`/executions/${executionId}/gates/b`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled:  Boolean(executionId),
    staleTime: 15_000,
    // Live Board pode passar refetchInterval (função ou ms) para o status de
    // aprovação refletir ao vivo sem refresh manual.
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

/**
 * useDecideGateB — mutation para aprovar/rejeitar/pedir esclarecimento no Gate B.
 *
 * comment é obrigatório em reject/clarify (validação no componente).
 *
 * Após sucesso invalida:
 *   - Gate B da execução (executionKeys.gateB)
 *   - Fila global de gates (gateKeys.all)
 *   - Status da execução (executionKeys.detail)
 *
 * 403 propagado via error.isForbidden para o componente exibir ForbiddenBanner.
 *
 * @param {string} executionId
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useDecideGateB(executionId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    /**
     * @param {{ decision: 'approve'|'reject'|'clarify', comment?: string }} payload
     */
    mutationFn: (payload) =>
      api.patch(`/executions/${executionId}/gates/b`, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: executionKeys.gateB(executionId) });
      queryClient.invalidateQueries({ queryKey: executionKeys.detail(executionId) });
      queryClient.invalidateQueries({ queryKey: gateKeys.all() });
    },
  });
}

// ─── Output (Onda D) ──────────────────────────────────────────────────────────

/**
 * useExecutionOutput — resultado completo de uma execução (5 abas).
 *
 * Resposta (Onda D):
 * {
 *   execution_id: string,
 *   pull_request: {
 *     pr_id: string, title: string, body: string,
 *     pr_url: string|null,         — URL real do PR no provedor (GitHub/GitLab/Bitbucket)
 *     commit_sha: string|null,
 *     head_branch: string|null,
 *     base_branch: string|null,
 *     provider: string|null,       — 'github' | 'gitlab' | 'bitbucket'
 *     external_pr_id: string|null, — número/ID do PR no provedor
 *     is_simulated: boolean,
 *     diff_url: string|null,
 *   },
 *   code_artifacts: Array<{        — primeira página (artifacts_limit artefatos)
 *     artifact_id, path, action: 'A'|'M'|'D',
 *     additions, deletions, is_simulated,
 *     content_sha, sandbox_run_id, diff
 *   }>,
 *   artifacts_total: number,       — total de artefatos para paginação
 *   compliance_checks: Array<{check_id, rule_name, status, detail, checked_at}>,
 *   audit_events: Array<{seq, event_type, actor_type, occurred_at}>,
 *   replay_events: Array<{seq, event_type, actor_type, occurred_at}>
 * }
 *
 * 404 normalizado para null (output não disponível ainda).
 * Nunca exibe wallet_txn_id.
 *
 * @param {string|null} executionId
 */
export function useExecutionOutput(executionId) {
  const api = useApi();

  return useQuery({
    queryKey: executionKeys.output(executionId),
    queryFn:  async () => {
      try {
        return await api.get(`/executions/${executionId}/output`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled:  Boolean(executionId),
    staleTime: 60_000, // output é imutável após gerado
  });
}

// ─── Artefatos de código paginados (Onda D) ───────────────────────────────────

/**
 * useExecutionArtifacts — artefatos de código paginados de uma execução.
 *
 * Endpoint: GET /executions/{id}/output?artifacts_limit=N&artifacts_offset=M
 * (o mesmo endpoint de output com parâmetros de paginação)
 *
 * Resposta: {
 *   code_artifacts: Array<{
 *     artifact_id: string,
 *     path: string,
 *     action: 'A' | 'M' | 'D',
 *     additions: number,
 *     deletions: number,
 *     is_simulated: boolean,
 *     content_sha: string|null,
 *     sandbox_run_id: string|null,
 *     diff: string|null,           — unified diff (patch format)
 *   }>,
 *   artifacts_total: number,
 * }
 *
 * 404 normalizado para null.
 * Nunca exibe wallet_txn_id ou sandbox_run_id sensível.
 *
 * @param {string|null}  executionId
 * @param {number}       [limit=10]  — quantos artefatos por página
 * @param {number}       [offset=0]  — deslocamento
 */
export function useExecutionArtifacts(executionId, limit = 10, offset = 0) {
  const api = useApi();

  return useQuery({
    queryKey: executionKeys.artifacts(executionId, limit, offset),
    queryFn:  async () => {
      try {
        const qs = new URLSearchParams({
          artifacts_limit:  String(limit),
          artifacts_offset: String(offset),
        });
        return await api.get(`/executions/${executionId}/output?${qs}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled:   Boolean(executionId),
    staleTime: 60_000, // artefatos são imutáveis após gerados
    keepPreviousData: true, // mantém dados da página anterior durante troca
  });
}

/**
 * useDeliveryStatus — status de entrega por repositório (verde no sandbox / PR aberto).
 * Alimenta o botão "Enviar ao GitHub": habilita quando há awaiting_approval; mostra os
 * PRs quando pushed. Faz polling enquanto houver algo em andamento (opts.refetchInterval).
 *
 * GET /executions/{id}/delivery-status
 */
export function useDeliveryStatus(executionId, { refetchInterval } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: [...executionKeys.detail(executionId), 'delivery-status'],
    queryFn:  () => api.get(`/executions/${executionId}/delivery-status`),
    enabled:  Boolean(executionId),
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

/**
 * usePushToGithub — dispara o envio ao GitHub da entrega já verificada (aprovação humana).
 * POST /executions/{id}/push-to-github → 202 (o PushDeliveryWorkflow empurra + abre PR).
 */
export function usePushToGithub() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (executionId) => api.post(`/executions/${executionId}/push-to-github`),
    onSuccess: (_data, executionId) => {
      queryClient.invalidateQueries({
        queryKey: [...executionKeys.detail(executionId), 'delivery-status'],
      });
      queryClient.invalidateQueries({ queryKey: executionKeys.output(executionId) });
    },
  });
}


/** Baixa um .md da entrega (rota `/executions/{id}/{rota}`) e dispara o download. */
async function baixarMarkdownDaEntrega(api, executionId, repo, rota, nomeArquivo) {
  const q = repo ? `?repo=${encodeURIComponent(repo)}` : '';
  const md = await api.getText(`/executions/${executionId}/${rota}${q}`);
  const blob = new Blob([md ?? ''], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Baixa o CORRECOES-PENDENTES.md da entrega (o que não fechou depois dos reforços,
 * pronto para um agente de IA finalizar). Dispara o download no navegador.
 * @param {object} api — cliente de `useApi()`
 * @param {string} executionId
 * @param {string} [repo]
 */
export async function baixarCorrecoesPendentes(api, executionId, repo) {
  return baixarMarkdownDaEntrega(api, executionId, repo,
    'correcoes-pendentes', 'CORRECOES-PENDENTES.md');
}

/**
 * Baixa o ATIVIDADES-COMPLEMENTARES.md da entrega: o que uma etapa identificou que falta
 * de OUTRA camada (tabela, endpoint, teste de obrigação). Não são erros — são atividades
 * complementares para o cliente rodar com um agente de IA.
 * @param {object} api — cliente de `useApi()`
 * @param {string} executionId
 * @param {string} [repo]
 */
export async function baixarAtividadesComplementares(api, executionId, repo) {
  return baixarMarkdownDaEntrega(api, executionId, repo,
    'atividades-complementares', 'ATIVIDADES-COMPLEMENTARES.md');
}

/**
 * Baixa o SETUP-IA-AMBIENTE.md da entrega: o roteiro para finalizar o projeto com um agente
 * de IA — ambiente, correções pendentes, atividades complementares e conferência, na ordem,
 * com o texto pronto para colar em cada passo.
 * @param {object} api — cliente de `useApi()`
 * @param {string} executionId
 * @param {string} [repo]
 */
export async function baixarSetupIaAmbiente(api, executionId, repo) {
  return baixarMarkdownDaEntrega(api, executionId, repo,
    'setup-ia-ambiente', 'SETUP-IA-AMBIENTE.md');
}

/** Hook: devolve `(executionId, repo?) => Promise` que baixa o SETUP-IA-AMBIENTE.md. */
export function useBaixarSetupIaAmbiente() {
  const api = useApi();
  return (executionId, repo) => baixarSetupIaAmbiente(api, executionId, repo);
}

/** Hook: devolve `(executionId, repo?) => Promise` que baixa o ATIVIDADES-COMPLEMENTARES.md. */
export function useBaixarAtividadesComplementares() {
  const api = useApi();
  return (executionId, repo) => baixarAtividadesComplementares(api, executionId, repo);
}

/**
 * Baixa a entrega da execução num .zip — o mesmo conteúdo que o "Enviar ao GitHub"
 * enviaria — para testar na própria máquina antes de abrir o Pull Request.
 * @param {object} api — cliente de `useApi()`
 * @param {string} executionId
 * @param {string} [repo] — restringe a um repositório (entrega multi-repo)
 */
export async function baixarProjetoDaEntrega(api, executionId, repo) {
  const q = repo ? `?repo=${encodeURIComponent(repo)}` : '';
  const { blob, filename } = await api.getBlob(`/executions/${executionId}/download${q}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `entrega-${String(executionId).slice(0, 8)}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Hook: devolve `(executionId, repo?) => Promise` que baixa a entrega em .zip. */
export function useBaixarProjetoDaEntrega() {
  const api = useApi();
  return (executionId, repo) => baixarProjetoDaEntrega(api, executionId, repo);
}

/** Hook: devolve `(executionId, repo?) => Promise` que baixa o CORRECOES-PENDENTES.md. */
export function useBaixarCorrecoesPendentes() {
  const api = useApi();
  return (executionId, repo) => baixarCorrecoesPendentes(api, executionId, repo);
}
